// src/encounter/lib/to-procedure-request.ts
// Build the wire bodies for the IPS-rail Procedures section (PROD-1266). REUSES the
// curl-verified CreateProcedureBody / UpdateProcedureBody contracts (patient-chart-api.ts,
// locked 2026-05-20) + the procedures.ts helpers (normalizeProcedureCode, emptyProcedureCoding)
// + the shared normalizePerformerDetails. Pure — mirrors to-condition-request / to-allergy-request.
//
// Actions:
//  - add    → POST /api/Patients/CreateProcedure (CreateProcedureBody; clinicalStatus:'Active').
//  - edit   → PUT  /api/Patients/procedure/{id}   (UpdateProcedureBody; round-trips the record).
//  - delete → PUT  /api/Patients/procedure/{id}   (isDeleted:true; preserve everything else).
//
// ⚠️ PROCEDURES-SPECIFIC WIRE GOTCHAS (curl-verified 2026-05-20, see CreateProcedureBody /
// UpdateProcedureBody type docs + add-procedure-editor.tsx):
//  - `code` uses **displayName** (PascalCase via CodeDisplayName), NOT `display`.
//    picked + stored codes are run through normalizeProcedureCode.
//  - `dataAbsentReason`: "" on CREATE, "n/a" on UPDATE (intentionally inconsistent).
//  - CREATE stamps BOTH `recorder` AND `asserter` = ctx.userId; UPDATE sets `recorder: null`
//    and PRESERVES the original `asserter` (block edit/delete when the asserter is missing —
//    mirrors the to-condition-request identifier/asserter guard).
//  - `category` is a bare STRING (null ok). `bodySite` + `reason` ship the empty-Coding
//    sentinel on CREATE, round-trip on UPDATE.
//  - `compilation` is a BACKEND TYPO of `complication` — preserve verbatim, do NOT rename.
//  - `createdDate: null` + `performerDetails: null` on CREATE, round-trip on UPDATE.
//  - `onsetDateTime` (ISO no-Z) is paired with `onsetDateTimeDisplay` (free-text).
import {
  type CodeDisplayName,
  type CreateProcedureBody,
  type PatientProcedure,
  type UpdateProcedureBody,
} from '@/types/patient-chart-api';
import {
  emptyProcedureCoding,
  normalizeProcedureCode,
} from '@/patients/lib/procedures';
import { normalizePerformerDetails } from '@/patients/components/chart/add-allergy-dialog';

export interface ProcedureRequestContext {
  /**
   * The encounter this item is being recorded IN — stamped on CREATE as the wire `encounter`.
   *
   * FLUTTER PARITY. Its shared clinical-info popup applies `data.encounter = encounterId` to EVERY
   * summary type before dispatching per type (`popup_widget.dart:152`), and the per-tab add paths do
   * the same (`allergies_tab.dart:785`, `history_tab.dart:1318,1490` → `encounter:
   * widget.encounterId`). Nearly every patient model carries the field — allergies, immunization,
   * conditions, procedures, medications, labs, flags, medical + social history.
   *
   * PA sent a hardcoded `encounter: null` here, so an item added on the pre-encounter had no record
   * of which visit produced it. Andrew, 2026-08-28: "when i call any api in the pre-encounter for
   * allergy, vaccine, family history, social history,.... we should add encounterId value."
   *
   * Optional on purpose. These builders are ENCOUNTER-ONLY — the patients-module chart has its own
   * path (`patients/services/use-create-allergy.ts` etc.), so an earlier version of this comment
   * claiming they "also serve the chart" was wrong. The reason it stays optional is that the
   * encounter itself can lack an id: a deep-link with no `createContext`, a Booked appointment
   * (where the open-phase create is deliberately gated), or an open-phase create that failed. In
   * those the rail still renders and still writes. Absent → `null`, exactly the previous behaviour.
   */
  encounterId?: string | null;
  patientId: string;
  /** Current user's userId GUID — stamped as `recorder` AND `asserter` on CREATE. */
  userId: string;
}

/** The dialog's resolved selection. */
export interface ProcedureFormValue {
  /** Selected procedure code (CPT master row — uses displayName, not display). */
  code: CodeDisplayName;
  /** ISO 8601 no-Z onset. Null when unknown. */
  onsetDateTime: string | null;
  /** User-facing onset display (DD/MM/YYYY etc). */
  onsetDateTimeDisplay: string | null;
  note?: string | null;
}

export type ProcedureAction =
  | { type: 'add'; value: ProcedureFormValue }
  | { type: 'edit'; existing: PatientProcedure; value: ProcedureFormValue }
  | { type: 'delete'; existing: PatientProcedure };

/** CREATE body. clinicalStatus always 'Active' on create. Both recorder + asserter = userId. */
export function buildCreateProcedureBody(
  value: ProcedureFormValue,
  ctx: ProcedureRequestContext,
): CreateProcedureBody {
  return {
    onsetDateTimeDisplay: value.onsetDateTimeDisplay,
    identifier: null,
    partOf: null,
    clinicalStatus: 'Active',
    // bare string label (or null) — NOT a Coding.
    category: null,
    code: normalizeProcedureCode(value.code),
    // Empty Coding sentinel (NOT null) on create.
    bodySite: emptyProcedureCoding(null),
    // Empty STRING on create — Procedures-specific (Update ships "n/a").
    dataAbsentReason: '',
    subject: ctx.patientId,
    // Procedures populates BOTH recorder AND asserter on create.
    recorder: ctx.userId,
    encounter: ctx.encounterId ?? null,
    performer: null,
    asserter: ctx.userId,
    reasonReference: null,
    reason: emptyProcedureCoding(null),
    outcome: 'n/a',
    // Backend typo preserved verbatim.
    compilation: 'n/a',
    onsetDateTime: value.onsetDateTime,
    note: value.note?.trim() || '',
    isDeleted: false,
    performerDetails: null,
    createdDate: null,
    speciality: null,
  };
}

/** Shared EDIT/DELETE body — round-trips the original record. recorder is NULL on update;
 *  the ORIGINAL asserter is preserved (audit trail of who originally recorded the procedure). */
function buildUpdateProcedureBody(
  existing: PatientProcedure,
  value: ProcedureFormValue | undefined,
  /** `encounterId`: the encounter this edit is happening IN — re-stamped onto the record,
   *  Flutter-style (see the field note below). Absent outside an encounter. */
  opts: { isDeleted: boolean; encounterId?: string | null },
): UpdateProcedureBody {
  return {
    onsetDateTimeDisplay: value ? value.onsetDateTimeDisplay : existing.onsetDateTimeDisplay ?? null,
    identifier: existing.identifier!,
    partOf: null,
    clinicalStatus: existing.clinicalStatus === 'Resolved' ? 'Resolved' : 'Active',
    category: existing.category ?? null,
    code: value
      ? normalizeProcedureCode(value.code)
      : normalizeProcedureCode(existing.code),
    bodySite: emptyProcedureCoding(existing.bodySite),
    // "n/a" on update — distinct from create's "".
    dataAbsentReason: 'n/a',
    subject: existing.subject ?? '',
    // Recorder is NULL on update (procedures wire quirk).
    recorder: null,
    /**
     * The CURRENT encounter wins, falling back to the record's own.
     *
     * FLUTTER PARITY (Andrew, 2026-08-28: "we will do as flutter"). Its shared popup assigns
     * `data.encounter = encounterId` BEFORE branching on `identifier != null`
     * (`popup_widget.dart:152`), so an edit re-stamps the item with the encounter it is being
     * edited IN — not the one that first recorded it. An earlier pass here preserved
     * `existing.encounter` on the reasoning that editing an old allergy shouldn't re-attribute it;
     * that was my call, and Andrew overruled it. It also fixes items PA wrote before the create-path
     * fix: they carry `encounter: null` and the next edit now heals them, where round-tripping would
     * have kept them null forever.
     *
     * THE `?? existing.encounter` IS NOT DECORATION. Flutter can assign unconditionally because its
     * popup guarantees an id first (`if (isBlank(encounterId)) createEncounter()`, initState). We
     * have no such guarantee: a deep-link with no `createContext`, a Booked appointment (open-phase
     * create gated), or a failed open-phase create all leave `encounterId` null while the rail still
     * renders and still writes. Assigning unconditionally in those cases would write null over a
     * real link and DESTROY the record's provenance. So: re-stamp when we have an encounter,
     * preserve when we don't.
     *
     * (An earlier version of this note justified the fallback with the patients-module chart. That
     * was wrong — the chart has its own request path and never calls these builders.)
     */
    encounter: opts.encounterId ?? existing.encounter ?? null,
    performer: null,
    // Preserve the ORIGINAL asserter — guarded non-null by buildProcedureRequest.
    asserter: existing.asserter!,
    reasonReference: null,
    reason: emptyProcedureCoding(existing.reason),
    outcome: existing.outcome?.trim() || 'n/a',
    // Backend typo preserved verbatim.
    compilation: existing.compilation?.trim() || 'n/a',
    onsetDateTime: value ? value.onsetDateTime : existing.onsetDateTime ?? null,
    note: value ? value.note?.trim() || '' : existing.note ?? '',
    isDeleted: opts.isDeleted,
    performerDetails: normalizePerformerDetails(existing.performerDetails),
    createdDate: existing.createdDate ?? null,
    speciality: null,
  };
}

export interface ProcedureRequest {
  kind: 'create' | 'update';
  /** Procedure GUID for the update path (`PUT /procedure/{id}`); undefined on create. */
  procedureId?: string;
  body: CreateProcedureBody | UpdateProcedureBody;
}

/**
 * Resolve a procedure action into endpoint + body.
 *  - add    → CREATE (always Active).
 *  - edit   → UPDATE.
 *  - delete → UPDATE soft-delete.
 * Returns null when the action is structurally invalid: edit/delete without an identifier, or
 * without the original `asserter` (which the update body must preserve — refuse to ship null
 * rather than substituting the editing user, mirroring add-procedure-editor's strict guard).
 */
export function buildProcedureRequest(
  action: ProcedureAction,
  ctx: ProcedureRequestContext,
): ProcedureRequest | null {
  if (action.type === 'add') {
    return { kind: 'create', body: buildCreateProcedureBody(action.value, ctx) };
  }
  // edit/delete must carry an identifier AND the original asserter to round-trip safely.
  if (!action.existing.identifier || !action.existing.asserter) return null;

  if (action.type === 'delete') {
    return {
      kind: 'update',
      procedureId: action.existing.identifier,
      body: buildUpdateProcedureBody(action.existing, undefined, { isDeleted: true, encounterId: ctx.encounterId }),
    };
  }

  return {
    kind: 'update',
    procedureId: action.existing.identifier,
    body: buildUpdateProcedureBody(action.existing, action.value, { isDeleted: false, encounterId: ctx.encounterId }),
  };
}
